import { getPlatform, isB2B, isFrancePlatform } from '@/core/settings/utils';
import type { PRProduct } from '@/core/tracking/interfaces/PRProduct';
import { MdScore } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { Detail } from '@/domains/productDiscovery/ProductPage/b2c/interfaces/Detail.model';
import { type Brand } from '@/domains/productDiscovery/ProductPage/modules/Brand/brand.model';
import { getTagLabels } from '@/domains/productDiscovery/ProductPage/utils/gtm/tagLabels';
import {
  ENERGY_ATTRIBUTE_ID,
  MAX_ENERGY_ATTRIBUTE_ID,
  MIN_ENERGY_ATTRIBUTE_ID,
} from '@/productDiscovery/ProductPage/common/components/AddToCart/IndexLabels/useEnergy';
import type { MarketDetail } from '@/productDiscovery/ProductPage/interfaces/Detail.model';

export function mapProductDetailToPRProduct(
  detail: MarketDetail,
  brand: Brand,
  extraFields: Pick<
    PRProduct,
    | 'pr_b2b_price_advantage_count'
    | 'pr_has_a_coupon'
    | 'pr_is_in_promo'
    | 'pr_md_count'
    | 'pr_md_top'
    | 'pr_shipping_options'
    | 'pr_shipping_options_available_count'
    | 'pr_shipping_options_count'
  >,
  mdScores?: Array<MdScore>,
): PRProduct {
  const hasB2bBenefits =
    isB2B() && detail.seller?.country === getPlatform().toUpperCase();

  const priceType = hasB2bBenefits ? 'amountWithoutVat' : 'amountWithVat';

  const finalRetailPrice = detail.price?.retailPrice
    ? detail.price?.retailPrice[priceType]
    : detail.price?.primaryPrice[priceType];

  const breadCrumbs = detail.breadcrumbs || [];

  const attributesLabels =
    detail.attributes?.filter((attr) => !!attr.isSustainablyRelevant) || [];

  const energyAttribute = detail.attributes?.find(
    (attribute) => attribute.id === ENERGY_ATTRIBUTE_ID,
  );
  const minEnergyAttribute = detail.attributes?.find(
    (attribute) => attribute.id === MIN_ENERGY_ATTRIBUTE_ID,
  );
  const maxEnergyAttribute = detail.attributes?.find(
    (attribute) => attribute.id === MAX_ENERGY_ATTRIBUTE_ID,
  );
  const hasEnergyLabel = Boolean(
    energyAttribute &&
      maxEnergyAttribute &&
      minEnergyAttribute &&
      detail.energy,
  );

  const m2 = 'm2';

  const isDegradedDetail = [
    'breadcrumbs',
    'price',
    'delivery',
    'seller',
    'description',
    'attributes',
  ].filter(
    (property) => !Object.prototype.hasOwnProperty.call(detail, property),
  );

  const lowestCategory = breadCrumbs[breadCrumbs.length - 1] || {};

  const finalPrimaryPrice = detail.price?.primaryPrice[priceType];

  const quantity = detail.stock?.minimumValue;

  return {
    pr_ad_tracking_data: undefined,

    pr_article_id: detail.articleId.toString(),

    pr_attributes_count: detail.attributes?.length,

    pr_base_price: finalRetailPrice,

    pr_brand_id: `${brand.id}`,

    pr_brand_name: brand.name || '',

    pr_bulk_threshold: undefined,

    pr_card_name: undefined,

    pr_category_level_id_1: breadCrumbs.at(0)?.id.toString(),

    pr_category_level_id_2: breadCrumbs.at(1)?.id.toString(),

    pr_category_level_id_3: breadCrumbs.at(2)?.id.toString(),

    pr_category_level_id_4: breadCrumbs.at(3)?.id.toString(),

    pr_category_level_name_1: breadCrumbs.at(0)?.name,

    pr_category_level_name_2: breadCrumbs.at(1)?.name,

    pr_category_level_name_3: breadCrumbs.at(2)?.name,

    pr_category_level_name_4: breadCrumbs.at(3)?.name,

    pr_cpc: undefined,

    pr_delivery_mode: undefined,

    pr_discount_amount: detail.price?.discountPercentage || 0,

    pr_eco_label_name: attributesLabels.map((attr) => attr.value),

    pr_has_calculator: isB2B()
      ? detail.price?.measurementPrice?.measurementUnit === m2 &&
        detail.unitCount > 0
      : false,

    pr_has_coupon_label: false,

    pr_has_eco_label: !!attributesLabels.length,

    pr_has_energy_label: hasEnergyLabel,

    pr_has_promo_label: undefined,

    pr_has_repairability_index: !!detail.repairabilityIndex,

    pr_has_responsible_table:
      isFrancePlatform() &&
      !!detail.attributes?.some((attr) => attr.isSustainablyRelevant),

    pr_has_variant: detail.variants?.list
      ? detail.variants?.list.length > 1
      : false,

    pr_has_warranty: !!detail.attributes?.some((attr) => attr.isWarranty),

    pr_is_best_quality_offer: detail.isBestQualityOffer,

    pr_is_cpt: undefined,

    pr_is_degraded: isDegradedDetail.length > 0,

    pr_is_local_product: undefined,

    pr_is_mf: Boolean(detail.delivery?.fulfillment),

    pr_is_pro: detail.isProProduct,

    pr_is_second_hand_product: detail.isSecondHand,

    pr_is_sponsored: undefined,

    pr_is_sustainable_product: undefined,

    pr_is_top_sales: detail.isOnTopSale,

    pr_list_name: undefined,

    pr_listing_fingerprint: undefined,

    pr_lowest_category_id: lowestCategory.id,

    pr_lowest_category_name: lowestCategory.name,

    pr_model_id: `${detail.modelId}`,

    pr_model_name: detail.title,

    pr_models_count: detail.totalModels,

    pr_name: detail.title,

    pr_position: undefined,

    // Will be affected by VAT cross border
    pr_price: finalPrimaryPrice,

    pr_price_excluding_vat: detail.price?.primaryPrice.amountWithoutVat,

    pr_product_id: `${detail.productId}`,

    pr_quantity: quantity,

    pr_rating: detail.averageRating,

    pr_rating_count: detail.totalRating,

    pr_reco_engine_name: undefined,

    pr_search_query: undefined,

    pr_seller_id: `${detail.seller?.id}`,

    pr_seller_name: detail.seller?.name,

    pr_shipping: undefined,

    pr_stock: detail.isSellable ? 'available' : 'out of stock',

    pr_total_ati: detail.price?.primaryPrice.amountWithVat,

    pr_variant: detail.variants?.selected
      ? Object.values(detail.variants.selected)
      : [],

    pr_variant_count: detail.variants?.list.length ?? 0,

    pr_variant_type: detail.variants?.selected
      ? Object.keys(detail.variants.selected)
      : [],

    pr_label: getTagLabels(detail as Detail, mdScores),

    ...extraFields,
  };
}
